// The overview's graphs along the recorded track, ported from the Android app's HeartRateProfile,
// CadenceProfile, PaceProfile and ProfileSeries so every platform draws the same lines.

import {
  interpolate,
  isMeasured,
  movingStretches,
  stretchCount,
  sum,
  valueAt,
  type Stretch,
} from './timeline';

export interface TimedReading {
  timestampMs: number;
  value: number;
}

/** `distance` is metres along the recorded track. */
export interface ProfileSample {
  distance: number;
  value: number;
}

/** One entry per track point, in recorded order. */
export interface TrackTimes {
  distances: number[];
  timeStampsMs: number[];
  elapsedMs: number[];
}

export interface PaceProfile {
  samples: ProfileSample[];
  /** The whole distance over the moving time. */
  movingSecondsPerMeter: number;
  /** The whole distance over the timer, stops and all, as the splits are timed. */
  overallSecondsPerMeter: number;
  fastestSecondsPerMeter: number;
  movingSeconds: number;
  /** Standing still with the timer running; a pause isn't counted. */
  stoppedSeconds: number;
}

export interface CadenceProfile {
  samples: ProfileSample[];
  totalSteps: number;
  averageStepsPerMinute: number;
  averageStrideMeters: number;
}

export const HEART_RATE_BUCKET_COUNT = 240;
export const ELEVATION_SAMPLE_COUNT = 150;

/** Slower than an hour per kilometre is standing still, not a pace. */
export const SLOWEST_SECONDS_PER_METER = 3.6;

/** Slower than this while recording counts as stopped. Every tool draws this line somewhere; this is HiiKER's. */
export const STOPPED_BELOW_KM_PER_HOUR = 1;

/** Beyond a flat-out run; anything higher is a miscount. */
export const FASTEST_STEPS_PER_MINUTE = 200;

/** Lower than this throughout is cycling or a car, not walking. */
export const MINIMUM_AVERAGE_STEPS_PER_MINUTE = 20;

/** A reading after a standstill or a late start only covers the walking just before it. */
export const LONGEST_STEP_READING_SPAN_SECONDS = 60;

function byTime(readings: TimedReading[]): TimedReading[] {
  return [...readings].sort((a, b) => a.timestampMs - b.timestampMs);
}

/** Where each ascending time falls on the track: the segment it's in and how far along it. */
function locate(times: number[], timeStampsMs: number[]): Array<{ segment: number; fraction: number }> {
  let segment = 0;
  return times.map((time) => {
    while (segment < timeStampsMs.length - 2 && timeStampsMs[segment + 1] <= time) segment++;
    const span = timeStampsMs[segment + 1] - timeStampsMs[segment];
    const fraction = span > 0 ? Math.min(Math.max((time - timeStampsMs[segment]) / span, 0), 1) : 0;
    return { segment, fraction };
  });
}

function linesUp(distances: number[], ...alongside: number[][]): boolean {
  const count = distances.length;
  return count >= 2 && distances[count - 1] > 0 && alongside.every((values) => values.length === count);
}

/** Readings averaged into equal stretches of distance, so a rest doesn't draw a vertical spike. */
export function heartRateProfile(
  readings: TimedReading[],
  track: TrackTimes,
  bucketCount = HEART_RATE_BUCKET_COUNT
): ProfileSample[] | null {
  const { distances, timeStampsMs } = track;
  if (readings.length === 0 || bucketCount <= 0 || !linesUp(distances, timeStampsMs)) return null;
  const total = distances[distances.length - 1];

  const buckets = Array.from({ length: bucketCount }, () => ({ count: 0, value: 0, distance: 0 }));
  const sorted = byTime(readings);
  locate(sorted.map((r) => r.timestampMs), timeStampsMs).forEach(({ segment, fraction }, i) => {
    const distance = interpolate(distances[segment], distances[segment + 1], fraction);
    const bucket = buckets[Math.min(Math.floor((distance / total) * bucketCount), bucketCount - 1)];
    bucket.count++;
    bucket.value += sorted[i].value;
    bucket.distance += distance;
  });

  const samples = buckets
    .filter((b) => b.count > 0)
    .map((b) => ({ distance: b.distance / b.count, value: b.value / b.count }));
  return samples.length >= 2 ? samples : null;
}

function stretchesOf(distances: number[], elapsedSeconds: number[], amounts?: number[]) {
  const total = distances[distances.length - 1];
  const count = stretchCount(total);
  const length = total / count;
  return { stretches: movingStretches(distances, elapsedSeconds, count, length, amounts), length };
}

function midpoint(index: number, length: number): number {
  return (index + 0.5) * length;
}

/** Moving pace over equal stretches of distance. */
export function paceProfile(track: Pick<TrackTimes, 'distances' | 'elapsedMs'>): PaceProfile | null {
  if (!linesUp(track.distances, track.elapsedMs)) return null;
  const elapsedSeconds = track.elapsedMs.map((ms) => ms / 1000);
  const { stretches, length } = stretchesOf(track.distances, elapsedSeconds);

  const measured = stretches.map((_, i) => i).filter((i) => isMeasured(stretches[i], length));
  if (measured.length < 2) return null;

  const samples = measured.map((i) => ({
    distance: midpoint(i, length),
    value: Math.min(stretches[i].seconds / stretches[i].meters, SLOWEST_SECONDS_PER_METER),
  }));
  const meters = sum(stretches.map((s) => s.meters));
  const timerSeconds = sum(stretches.map((s) => s.seconds));
  const { movingSeconds, stoppedSeconds } = movingTotals(track.distances, elapsedSeconds);
  return {
    samples,
    movingSecondsPerMeter: (movingSeconds > 0 ? movingSeconds : timerSeconds) / meters,
    overallSecondsPerMeter: timerSeconds / meters,
    fastestSecondsPerMeter: samples.reduce((fastest, s) => Math.min(fastest, s.value), Infinity),
    movingSeconds,
    stoppedSeconds,
  };
}

/**
 * The timer split into moving and stopped: a segment slower than STOPPED_BELOW_KM_PER_HOUR is
 * standing still. Pauses and the jumps between legs take no time on `elapsedSeconds`, so they are in neither.
 */
export function movingTotals(distances: number[], elapsedSeconds: number[]): { movingSeconds: number; stoppedSeconds: number } {
  const totals = { movingSeconds: 0, stoppedSeconds: 0 };
  for (let i = 1; i < distances.length; i++) {
    const seconds = elapsedSeconds[i] - elapsedSeconds[i - 1];
    if (seconds <= 0) continue;
    const meters = Math.max(distances[i] - distances[i - 1], 0);
    if (meters * 3.6 < STOPPED_BELOW_KM_PER_HOUR * seconds) {
      totals.stoppedSeconds += seconds;
    } else {
      totals.movingSeconds += seconds;
    }
  }
  return totals;
}

/**
 * Each reading's steps spread evenly over the moving time since the reading before it, as the steps
 * taken on each segment of the track (the entry at an index ends at that point).
 */
export function stepsPerSegment(readings: TimedReading[], timeStampsMs: number[], elapsedSeconds: number[]): number[] {
  const spans: Array<{ start: number; end: number; steps: number }> = [];
  const sorted = byTime(readings);
  locate(sorted.map((r) => r.timestampMs), timeStampsMs).forEach(({ segment, fraction }, i) => {
    const end = interpolate(elapsedSeconds[segment], elapsedSeconds[segment + 1], fraction);
    const previousEnd = spans.length > 0 ? spans[spans.length - 1].end : 0;
    spans.push({ start: Math.max(previousEnd, end - LONGEST_STEP_READING_SPAN_SECONDS), end, steps: sorted[i].value });
  });

  let next = 0;
  let completed = 0;
  const cumulative = elapsedSeconds.map((time) => {
    while (next < spans.length && spans[next].end <= time) {
      completed += spans[next].steps;
      next++;
    }
    const span = spans[next];
    const partial = span && span.start < time ? (span.steps * (time - span.start)) / (span.end - span.start) : 0;
    return completed + partial;
  });
  return cumulative.map((value, i) => (i === 0 ? 0 : value - cumulative[i - 1]));
}

/** Steps per moving minute over the same stretches as pace; null when it can't be walking. */
export function cadenceProfile(readings: TimedReading[], track: TrackTimes): CadenceProfile | null {
  const { distances, timeStampsMs, elapsedMs } = track;
  if (readings.length === 0 || !linesUp(distances, timeStampsMs, elapsedMs)) return null;
  const elapsedSeconds = elapsedMs.map((ms) => ms / 1000);
  const { stretches, length } = stretchesOf(distances, elapsedSeconds, stepsPerSegment(readings, timeStampsMs, elapsedSeconds));

  // A walked stretch without a single step is a gap in the counting, not a standstill.
  const counted = (s: Stretch) => isMeasured(s, length) && s.seconds > 0 && s.amount > 0;
  const measured = stretches.map((_, i) => i).filter((i) => counted(stretches[i]));
  if (measured.length < 2) return null;

  const steps = sum(measured.map((i) => stretches[i].amount));
  const average = steps / (sum(measured.map((i) => stretches[i].seconds)) / 60);
  if (average < MINIMUM_AVERAGE_STEPS_PER_MINUTE) return null;

  return {
    samples: measured.map((i) => ({
      distance: midpoint(i, length),
      value: Math.min(stretches[i].amount / (stretches[i].seconds / 60), FASTEST_STEPS_PER_MINUTE),
    })),
    totalSteps: sum(readings.map((r) => r.value)),
    averageStepsPerMinute: Math.min(average, FASTEST_STEPS_PER_MINUTE),
    averageStrideMeters: sum(measured.map((i) => stretches[i].meters)) / steps,
  };
}

/** Height averaged over equal stretches; null for a track with no heights, which reads as flat zero. */
export function elevationProfile(
  distances: number[],
  altitudes: Array<number | null | undefined>
): ProfileSample[] | null {
  const total = distances[distances.length - 1];
  if (!(total > 0) || distances.length !== altitudes.length) return null;

  const count = Math.min(ELEVATION_SAMPLE_COUNT, distances.length);
  const sums = new Array(count).fill(0);
  const counts = new Array(count).fill(0);
  distances.forEach((distance, i) => {
    const altitude = altitudes[i];
    if (altitude === null || altitude === undefined || Number.isNaN(altitude)) return;
    const index = Math.min(Math.floor((distance / total) * count), count - 1);
    sums[index] += altitude;
    counts[index]++;
  });

  const samples = sums
    .map((_, i) => i)
    .filter((i) => counts[i] > 0)
    .map((i) => ({ distance: ((i + 0.5) * total) / count, value: sums[i] / counts[i] }));
  if (samples.length < 2 || samples.every((s) => s.value === 0)) return null;
  return samples;
}

/** The moving time at a distance along the track, in milliseconds. */
export function elapsedMsAt(distance: number, track: Pick<TrackTimes, 'distances' | 'elapsedMs'>): number {
  return valueAt(distance, track.distances, track.elapsedMs);
}
